import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DividendsTieOut, TieOutFigure } from '../../dividendsMeta';

type TieOutBarProps = {
    /** Null while the chart of accounts is still loading. */
    tieOut: DividendsTieOut | null;
};

const Side: React.FC<{
    label: string;
    accountsLabel: string;
    figure: TieOutFigure;
    differenceLabel: string;
}> = ({ label, accountsLabel, figure, differenceLabel }) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span
                className="text-base font-semibold tabular-nums text-foreground"
                {...getNodeTestIdProp(
                    `dividends-tie-out-register-${figure.slot}`,
                )}
            >
                {formatGel(figure.registerTetri)}
            </span>
            <Icon
                aria-hidden
                className="size-3.5 shrink-0 text-muted"
                icon={
                    figure.agrees
                        ? 'gravity-ui:equal'
                        : 'gravity-ui:arrow-right-arrow-left'
                }
            />
            <span
                className="text-base font-semibold tabular-nums text-foreground"
                {...getNodeTestIdProp(
                    `dividends-tie-out-ledger-${figure.slot}`,
                )}
            >
                {formatGel(figure.ledgerTetri)}
            </span>
        </div>
        <span className="text-xs text-muted">{accountsLabel}</span>
        {figure.agrees ? null : (
            // Foreground, not danger. Paying a shareholder debits dividends
            // payable while `declaredUnpaidDividends` only ever grows, so the
            // first payment puts this row into a difference that no action can
            // clear — and a figure that is permanently red is one people learn
            // to stop reading. See `computeTieOut`.
            <span
                className="text-xs font-medium text-foreground"
                {...getNodeTestIdProp(
                    `dividends-tie-out-difference-${figure.slot}`,
                )}
            >
                {differenceLabel}
            </span>
        )}
    </div>
);

/**
 * The register-to-ledger tie-out — a first-class element, not a debug aid.
 *
 * The dividends register is a subsidiary ledger: what it says is declared and
 * unpaid must equal the balance of dividends payable, and its accrued taxes must
 * equal the two tax payables. If it can drift, it is decorative.
 *
 * Both sides are already on the same basis: `declaredUnpaidDividends` is the
 * **net** the entry credited to dividends payable, not the gross awarded, so
 * `computeTieOut` compares the two figures as they come. Netting the withholding
 * off first — which it used to do, against the mock — would report a permanent,
 * spurious difference of exactly the withholding.
 */
export const TieOutBar: React.FC<TieOutBarProps> = ({ tieOut }) => {
    const { t } = useTranslation('pages');

    if (tieOut === null) {
        return (
            <div
                className="flex items-center gap-2 rounded-xl bg-surface-secondary px-4 py-3 text-sm text-muted"
                {...getNodeTestIdProp('dividends-tie-out-loading')}
            >
                <Icon aria-hidden icon="gravity-ui:circle-info" />
                {t('dividends.tieOut.loading')}
            </div>
        );
    }

    // The accounts this register maps to are not in the chart, so there is no
    // ledger side to compare against. Say that, rather than drawing a difference
    // equal to the whole register: a tie-out that reports a difference which
    // isn't real discredits the one thing this bar exists to provide.
    if (!tieOut.isResolved) {
        return (
            <div
                className="flex items-center gap-2 rounded-xl bg-surface-secondary px-4 py-3 text-sm text-muted"
                {...getNodeTestIdProp('dividends-tie-out-pending')}
            >
                <Icon aria-hidden icon="gravity-ui:circle-info" />
                {t('dividends.tieOut.noAccounts')}
            </div>
        );
    }

    return (
        <section
            aria-label={t('dividends.tieOut.title')}
            className="flex flex-col gap-4 rounded-xl bg-surface-secondary px-4 py-3 lg:flex-row lg:items-center lg:justify-between"
            {...getNodeTestIdProp('dividends-tie-out')}
        >
            <div className="flex flex-wrap items-start gap-x-10 gap-y-4">
                {tieOut.figures.map((figure) => (
                    <Side
                        accountsLabel={t('dividends.tieOut.against', {
                            account: figure.accountCode,
                        })}
                        differenceLabel={t('dividends.tieOut.offBy', {
                            amount: formatGel(figure.differenceTetri),
                        })}
                        figure={figure}
                        key={figure.slot}
                        label={t(figure.labelKey)}
                    />
                ))}
            </div>

            <div
                className={cn(
                    'flex w-fit items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold',
                    tieOut.inAgreement
                        ? 'bg-success-soft text-success-soft-foreground'
                        : 'bg-surface text-foreground',
                )}
                {...getNodeTestIdProp('dividends-tie-out-indicator')}
            >
                <Icon
                    aria-hidden
                    icon={
                        tieOut.inAgreement
                            ? 'gravity-ui:circle-check'
                            : 'gravity-ui:scales'
                    }
                />
                {tieOut.inAgreement
                    ? t('dividends.tieOut.inAgreement')
                    : t('dividends.tieOut.outOfAgreement')}
            </div>
        </section>
    );
};
